/* eslint-disable no-restricted-imports */
import {RankedDivisionTypes, RankedMMRState, RankedSubDivisionTypes} from '@colonist/leaderboard-types'
import {Button, T, t} from '@colonist/ui'
import React, {useState, useEffect} from 'react'
import {Avatar} from '../../../../common/Avatar'
import styles from './MMRChange.module.scss'
import rankedStyles from '../UIGameEndRankedScreen.module.scss'
import {clsx} from 'clsx'
import {AnimatedNumber} from '../../../../common/AnimatedNumber'
import {RankedUtils} from '../../../../../../../code/shared/utils/RankedUtils'
import {sleep} from '../../../../utils/helpers'
import {FADE_IN_ANIMATION_DELAYS} from '../../constants'
import {UIImagePathController} from '../../../../../../../code/client/shared/scripts/UIEnums'
import {FadeInContainer} from '../../../../common/FadeInContainer'

const ANIMATION_PHASE_TRANSITION_DURATION = 500
const ANIMATION_PROGRESS_BAR_DURATION = 1000

const Division = ({divisionState}: {divisionState: {
    division: RankedDivisionTypes
    subdivision: RankedSubDivisionTypes
}}) => {
    if(divisionState.division === RankedDivisionTypes.None) {
        return null
    }
    return <div className={clsx(styles.division)}>
        <Avatar
            className={styles.divisionImage}
            hideBackground
            image={UIImagePathController.getRankedDivisionImage(divisionState.division)}
        />
        <Avatar
            className={styles.subDivisionImage}
            hideBackground
            image={UIImagePathController.getRankedSubdivisionImage(divisionState.subdivision)}
        />
    </div>
}

export const MMRChange = ({
    onContinue,
    rankedState,
    disableAnimation,
}: {
    onContinue: VoidFunction
    rankedState: RankedMMRState
    disableAnimation: boolean
}) => {
    const promotionText = t(rankedState.promotionDemotionText)
    const mmrChangeText = rankedState.getEndGameScreenMMRChangeText()
    const currentMMRText = rankedState.getEndGameScreenText()
    const isIncreaseMMR = rankedState.mostRecentChangeInValue > 0
    const isLostMMR = rankedState.currentMMR < rankedState.previousMMR

    const isPromoted = RankedUtils.isFirstRankedStateHigherThan(rankedState.currentDivisionState, rankedState.previousDivisionState)
    const isDemoted = RankedUtils.isFirstRankedStateHigherThan(rankedState.previousDivisionState, rankedState.currentDivisionState)
    const isPromotedOrDemoted = isPromoted || isDemoted
    const shouldRunDemotePromoteAnimation = !disableAnimation

    // 1. Normal MMR change at the current division (no promotion/demotion)
    let upperBoundary = rankedState.currentDivisionState.promotionDivisionBoundary.boundary
    let lowerBoundary = rankedState.currentDivisionState.demotionDivisionBoundary.boundary
    let previousMMR = rankedState.previousMMR
    let previousProgress = ((previousMMR - lowerBoundary) / (upperBoundary - lowerBoundary)) * 100
    let currentMMR = rankedState.currentMMR
    let currentProgress = ((currentMMR - lowerBoundary) / (upperBoundary - lowerBoundary)) * 100
    let currentDivisionState = rankedState.currentDivisionState
    let promotionDivisionBoundary = rankedState.currentDivisionState.promotionDivisionBoundary
    let demotionDivisionBoundary = rankedState.currentDivisionState.demotionDivisionBoundary

    // 2. Promotion: MMR change from current to upper division
    if(isPromoted && shouldRunDemotePromoteAnimation) {
        upperBoundary = rankedState.previousDivisionState.promotionDivisionBoundary.boundary
        lowerBoundary = rankedState.previousDivisionState.demotionDivisionBoundary.boundary
        previousMMR = rankedState.previousMMR
        previousProgress = ((previousMMR - lowerBoundary) / (upperBoundary - lowerBoundary)) * 100
        currentMMR = upperBoundary
        currentProgress = 100
        currentDivisionState = rankedState.previousDivisionState
        promotionDivisionBoundary = rankedState.previousDivisionState.promotionDivisionBoundary
        demotionDivisionBoundary = rankedState.previousDivisionState.demotionDivisionBoundary
    }

    // 3. Demotion: MMR change from current to lower division
    if(isDemoted && shouldRunDemotePromoteAnimation) {
        upperBoundary = rankedState.previousDivisionState.promotionDivisionBoundary.boundary
        lowerBoundary = rankedState.previousDivisionState.demotionDivisionBoundary.boundary
        previousMMR = rankedState.previousMMR
        previousProgress = ((previousMMR - lowerBoundary) / (upperBoundary - lowerBoundary)) * 100
        currentMMR = lowerBoundary
        currentProgress = 0
        currentDivisionState = rankedState.previousDivisionState
        promotionDivisionBoundary = rankedState.previousDivisionState.promotionDivisionBoundary
        demotionDivisionBoundary = rankedState.previousDivisionState.demotionDivisionBoundary
    }

    const [animationPhase, setAnimationPhase] = useState(1)
    const [animationPhaseConfig, setAnimationPhaseConfig] = useState({
        previousMMR: previousMMR,
        currentMMR: currentMMR,
        currentDivisionState: currentDivisionState,
        promotionDivisionBoundary: promotionDivisionBoundary,
        demotionDivisionBoundary: demotionDivisionBoundary,
    })

    const initialProgressWidth = disableAnimation ? currentProgress : previousProgress
    const [progressWidth, setProgressWidth] = useState(initialProgressWidth)
    const [showContinueButton, setShowContinueButton] = useState(isLostMMR)
    const [isHiddenProgress, setIsHiddenProgress] = useState(false)

    const numberAnimationDurationDelay = animationPhase === 1 ?
        FADE_IN_ANIMATION_DELAYS.content + FADE_IN_ANIMATION_DELAYS.transitionDuration + ANIMATION_PHASE_TRANSITION_DURATION
        : ANIMATION_PHASE_TRANSITION_DURATION

    /**
     * Animation phase 1 runs for all cases
     */
    const animationPhase1 = async() => {
        if(disableAnimation) {
            setShowContinueButton(true)
            return
        }
        await sleep(numberAnimationDurationDelay)
        setProgressWidth(currentProgress)
        if(isPromotedOrDemoted) {
            setAnimationPhase(2)
            await sleep(ANIMATION_PROGRESS_BAR_DURATION + ANIMATION_PHASE_TRANSITION_DURATION)
            void animationPhase2()
        } else {
            await sleep(ANIMATION_PROGRESS_BAR_DURATION)
            setShowContinueButton(true)
        }
    }

    /**
     * Animation phase 2 runs for promotion and demotion cases
     */
    const animationPhase2 = async() => {
        setIsHiddenProgress(true)

        await sleep(ANIMATION_PHASE_TRANSITION_DURATION)

        const current = rankedState.currentMMR
        const upper = rankedState.currentDivisionState.promotionDivisionBoundary.boundary
        const lower = rankedState.currentDivisionState.demotionDivisionBoundary.boundary
        const newPreviousProgress = isPromoted ? 0 : 100
        const newCurrentProgress = ((current - lower) / (upper - lower)) * 100

        setProgressWidth(newPreviousProgress)

        await sleep(ANIMATION_PHASE_TRANSITION_DURATION)

        setAnimationPhaseConfig({
            previousMMR: isPromoted ? lower : upper,
            currentMMR: current,
            currentDivisionState: rankedState.currentDivisionState,
            promotionDivisionBoundary: rankedState.currentDivisionState.promotionDivisionBoundary,
            demotionDivisionBoundary: rankedState.currentDivisionState.demotionDivisionBoundary,
        })

        setIsHiddenProgress(false)
        await sleep(ANIMATION_PHASE_TRANSITION_DURATION)
        setProgressWidth(newCurrentProgress)
        await sleep(ANIMATION_PROGRESS_BAR_DURATION)
        setShowContinueButton(true)
    }

    useEffect(() => {
        void animationPhase1()
    }, [])

    return (
        <div className={styles.container}>
            <div>
                {promotionText && <div className={styles.promotionText}>
                    {promotionText}
                </div>}
                <div className={clsx(styles.mmrChangeText, {
                    [styles.increase]: isIncreaseMMR,
                    [styles.decrease]: !isIncreaseMMR,
                })}>
                    <T id={mmrChangeText} />
                </div>
                <div className={styles.currentMMRText}>
                    <T id={currentMMRText.title} />
                    <T id={currentMMRText.description} />
                </div>
            </div>
            <div
                className={clsx(styles.sliderContainer, {[styles.hidden]: isHiddenProgress})}
                style={{transitionDuration: isHiddenProgress ? '0' : `${ANIMATION_PHASE_TRANSITION_DURATION}ms`}}>
                <Division divisionState={{
                    division: animationPhaseConfig.demotionDivisionBoundary.division,
                    subdivision: animationPhaseConfig.demotionDivisionBoundary.subdivision,
                }} />
                <div className={clsx(styles.slider)}>
                    <div
                        className={clsx(styles.progress, {
                            [styles.animated]: !disableAnimation,
                        })}
                        style={{
                            transitionDuration: isHiddenProgress ? '0s' : `${ANIMATION_PROGRESS_BAR_DURATION}ms`,
                            width: `${progressWidth}%`}} />
                    <div
                        className={clsx(styles.division, {
                            [styles.animated]: !disableAnimation,
                        })}
                        style={{
                            transitionDuration: isHiddenProgress ? '0s' : `${ANIMATION_PROGRESS_BAR_DURATION}ms`,
                            left: `${progressWidth}%`}}>
                        <div className={styles.currentMMR}>
                            <AnimatedNumber
                                currentNumber={animationPhaseConfig.previousMMR}
                                delay={numberAnimationDurationDelay}
                                duration={ANIMATION_PROGRESS_BAR_DURATION}
                                paused={isHiddenProgress}
                                disableAnimation={disableAnimation}
                                targetNumber={animationPhaseConfig.currentMMR} />
                        </div>
                        <Division divisionState={{
                            division: animationPhaseConfig.currentDivisionState.divisionType,
                            subdivision: animationPhaseConfig.currentDivisionState.subdivisionType,
                        }} />
                    </div>
                </div>
                <Division divisionState={{
                    division: animationPhaseConfig.promotionDivisionBoundary.division,
                    subdivision: animationPhaseConfig.promotionDivisionBoundary.subdivision,
                }} />
            </div>
            <FadeInContainer
                animationDurationMs={FADE_IN_ANIMATION_DELAYS.transitionDuration}
                animationPlayState={showContinueButton ? 'running' : 'paused'}
                disableAnimation={disableAnimation}
                style={{
                    visibility: showContinueButton ? 'visible' : 'hidden',
                }}
            >
                <Button
                    className={rankedStyles.continueButton}
                    onClick={onContinue}
                >
                    <T id={{key: 'strings:game.endGame.footer.continue'}}/>
                </Button>
            </FadeInContainer>
        </div>
    )
}
